import React from "react";
import confetti from "canvas-confetti";
import { motion } from "framer-motion";

import {
  getCompletionCopy,
  BRAND_VIOLET,
  usePrefersReducedMotion,
  vibrate,
} from "./cards/cardCommon";

/**
 * Final "you're done!" card rendered as the slide after the last deck card.
 *
 * Confetti fires once when the user lands here (suppressed under
 * prefers-reduced-motion). Stats line shows: total cards, trivia score
 * (only when there are trivia cards in the deck — no point showing
 * "0 of 0 right" otherwise).
 *
 * The actual completion record is submitted to the server by the player
 * shell when this slide becomes active — this component only renders
 * the UI. Decoupling means the shell can route around server validation
 * failures (e.g., scroll back to an unanswered trivia card) without this
 * component having to know about that flow.
 */
export function CompletionScreen({
  deck,
  cards,
  responsesByCardUuid,
  language,
  isActive,
  // Public share page: no "Continue" (nowhere to go) — Replay becomes the
  // primary action.
  isPublic = false,
  onContinue,
  onReplay,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const headline = React.useMemo(
    () => getCompletionCopy(language),
    // headline is captured on first render so it doesn't change on re-renders
    // while still showing variety across deck completions
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const totalCards = cards?.length || 0;
  const quizCards = (cards || []).filter((c) => c.card_type === "quiz");
  const correctCount = quizCards.filter(
    (c) => responsesByCardUuid?.[c.uuid]?.is_correct,
  ).length;

  // Confetti on activation. Bind canvas-confetti to a canvas inside this
  // component rather than letting it append one to document.body — the
  // default canvas uses z-index 100, but the player is at z-1400, so a
  // body-level canvas would render behind the player and be invisible.
  // A local canvas inherits the player's stacking context.
  const canvasRef = React.useRef(null);
  const confettiFnRef = React.useRef(null);
  const hasFiredConfettiRef = React.useRef(false);

  React.useEffect(() => {
    if (!canvasRef.current) return;
    confettiFnRef.current = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true,
    });
  }, []);

  React.useEffect(() => {
    if (!isActive) {
      hasFiredConfettiRef.current = false;
      return;
    }
    if (reducedMotion || hasFiredConfettiRef.current) return;
    if (!confettiFnRef.current) return;
    hasFiredConfettiRef.current = true;
    vibrate([20, 30, 60]);
    confettiFnRef.current({
      particleCount: 90,
      spread: 75,
      origin: { y: 0.55 },
      colors: [BRAND_VIOLET, "#ffffff", "#fde68a", "#fbcfe8"],
      ticks: 200,
    });
  }, [isActive, reducedMotion]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Localized labels

  const lang = (language || "").toLowerCase();
  const continueLabel =
    lang === "spanish" ? "Continuar" : lang === "chinese" ? "继续" : "Continue";
  const replayLabel =
    lang === "spanish" ? "Reintentar" : lang === "chinese" ? "重玩" : "Replay";
  const cardsLabel = (count) => {
    if (lang === "spanish") return `${count} ${count === 1 ? "tarjeta" : "tarjetas"}`;
    if (lang === "chinese") return `${count} 张卡片`;
    return `${count} ${count === 1 ? "card" : "cards"}`;
  };
  const scoreLabel = (correct, total) => {
    if (lang === "spanish") return `${correct} de ${total} correctas`;
    if (lang === "chinese") return `${correct}/${total} 正确`;
    return `${correct} of ${total} right`;
  };

  return (
    <div className="relative w-full h-full bg-black flex flex-col items-center justify-center px-8 text-white text-center select-none">
      {/* Confetti canvas — inset-0, behind the celebration content but
          inside the player's stacking context so it actually shows. */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
      />
      <motion.div
        initial={{ scale: 0.7, opacity: 0 }}
        animate={isActive ? { scale: 1, opacity: 1 } : { scale: 0.7, opacity: 0 }}
        transition={{
          type: reducedMotion ? "tween" : "spring",
          stiffness: 220,
          damping: 18,
        }}
        className="flex flex-col items-center"
      >
        <div className="text-7xl mb-6">🎉</div>
        <h1 className="text-4xl font-bold mb-4 leading-tight">{headline}</h1>
        <div className="text-base opacity-80 mb-12">
          {cardsLabel(totalCards)}
          {quizCards.length > 0 && (
            <>
              {" · "}
              {scoreLabel(correctCount, quizCards.length)}
            </>
          )}
        </div>
      </motion.div>

      {/* Continue exits the deck — omitted on the public share page (nowhere to
          go without an app shell). */}
      {!isPublic && (
        <button
          type="button"
          onClick={onContinue}
          className="w-full max-w-sm rounded-xl py-4 text-lg font-semibold mb-3 hover:opacity-90 transition-opacity"
          style={{ backgroundColor: BRAND_VIOLET }}
        >
          {continueLabel}
        </button>
      )}
      {/* Replay restarts the deck. Primary action on the public page; a
          secondary link otherwise. */}
      <button
        type="button"
        onClick={onReplay}
        className={
          isPublic
            ? "w-full max-w-sm rounded-xl py-4 text-lg font-semibold mb-3 hover:opacity-90 transition-opacity"
            : "text-sm text-white/70 hover:text-white underline transition-colors"
        }
        style={isPublic ? { backgroundColor: BRAND_VIOLET } : undefined}
      >
        {replayLabel}
      </button>
    </div>
  );
}
